import React, { useEffect, useMemo } from 'react';
import { useIsFocused, useRoute } from '@react-navigation/native';
import type { AddressType } from '@sweetgreen/garnish';
import { useUrqlPolling, useUserLocation } from '@sweetgreen/garnish';

import type { PartialLineItem } from '@order/graphql';
import type {
  ChallengeCelebration,
  CourierDetails,
  DeliveryPreferenceType,
  Ledger,
  OrderStatus,
} from '@order/graphql';
import {
  FlattenedOrderStatuses,
  OrderType,
  useChallengeCelebrationQuery,
} from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

import { ADDRESS_TYPE } from '../../components/Address/constants';
import { useReorderFromOrder } from '../ReorderingScreen';
import {
  useOrderStatusPollingQuery,
  useOrderStatusQuery,
} from './GraphQL/OrderStatus.query.generated';

export const useOrderStatusData = (): OrderStatusScreenData => {
  // ─────────────────────────── Request ───────────────────────────────
  const { params } = useRoute();
  const { orderId } = params as { readonly orderId: string };
  const [{ data, fetching, stale }, refetchStatus] = useOrderStatusQuery({
    variables: { id: orderId },
    requestPolicy: 'network-only',
  });
  const isRenderedOnScreen = useIsFocused();

  React.useEffect(() => {
    if (isRenderedOnScreen) refetchStatus();
  }, [isRenderedOnScreen, refetchStatus]);

  const { celebration, fetchingCelebration } = useChallengeCelebration(orderId);

  const response = data?.orderStatus as OrderStatus;
  const isInvalidOrderStatus =
    data?.orderStatus?.__typename === 'InvalidOrder' ||
    data?.orderStatus === null;

  // ─────────────────────────── Parsing ───────────────────────────────
  const order = response?.order;
  const orderStatus =
    response?.flattenedOrderStatus ?? FlattenedOrderStatuses.Failed;
  const restaurant = order?.restaurant;
  const deliveryDetails = order?.deliveryOrderDetail;
  const courierDetails = response?.courierDetails;
  const deliveryAddress = deliveryDetails?.address;
  const isRestaurantOutpost = restaurant?.isOutpost;
  const hasSeveralDropoffLocation =
    (restaurant?.availableDropOffLocations?.length ?? 0) > 1;
  const dropoffLocation =
    isRestaurantOutpost && hasSeveralDropoffLocation
      ? order?.dropoffLocation ?? undefined
      : undefined;
  const orderType = getOrderType(order?.orderType, isRestaurantOutpost);
  const { status, courier } = useOrderStatusCourierPolling(
    orderId,
    orderType,
    orderStatus,
    courierDetails,
  );
  const { geolocation } = useUserLocation({
    pollingInterval: GEO_POLLING_INTERVAL,
  });
  const { latitude: userLat, longitude: userLon } = geolocation ?? {};

  // ─────────────────────────── Reorder ───────────────────────────────
  const onReorder = useReorderFromOrder(order);

  // ──────────────────────────── Data ────────────────────────────────
  return {
    isRenderedOnScreen,
    fetching: fetching || stale || fetchingCelebration,
    deliveryAddressName: deliveryAddress?.name ?? undefined,
    deliveryAddressStreet: deliveryAddress?.street,
    deliveryAddressSecondaryStreet:
      deliveryAddress?.secondaryStreet ?? undefined,
    deliveryAddressCity: deliveryAddress?.city,
    deliveryAddressState: deliveryAddress?.state,
    deliveryAddressZipCode: deliveryAddress?.zipCode,
    deliveryAddressNotes: deliveryAddress?.notes ?? undefined,
    deliveryAddressLatitude: deliveryAddress?.latitude,
    deliveryAddressLongitude: deliveryAddress?.longitude,
    deliveryAddressType: getAddressType(deliveryAddress?.name),
    deliveryPreference: deliveryAddress?.deliveryPreference,
    deliveryEstimatedTime: deliveryDetails?.estimatedDeliveryTime ?? undefined,

    locationName: order?.restaurant?.name,
    locationAddress: order?.restaurant?.address,
    locationCity: order?.restaurant?.city,
    locationState: order?.restaurant?.state,
    locationZipCode: order?.restaurant?.zipCode,
    locationLatitude: order?.restaurant?.latitude,
    locationLongitude: order?.restaurant?.longitude,
    dropoffLocation,

    orderId,
    orderType,
    orderStatus: status,
    orderTotal: order?.total ?? undefined,
    orderWantedTime: order?.wantedTime,
    orderCanTrackOrderStatus: Boolean(order?.canTrackOrderStatus),
    orderPendingFeedback: Boolean(order?.isPendingFeedback),
    orderLedger: order?.ledger ?? {},
    orderLineItems: order?.lineItems ?? [],
    vendorId: order?.vendorId,

    customerName: useLocalizationContext().t('general.you'),
    customerLatitude: userLat ?? 0,
    customerLongitude: userLon ?? 0,

    courierName: courier?.name,
    courierPhone: courier?.phone,
    courierLatitude: courier?.latitude,
    courierLongitude: courier?.longitude,

    celebration: celebration?.challengeCelebration,

    isInvalidOrderStatus,
    onReorder,
  };
};

const getAddressType = (name?: string | null): 'home' | 'work' | 'custom' => {
  if (name === ADDRESS_TYPE.HOME) return 'home';

  if (name === ADDRESS_TYPE.WORK) return 'work';

  return 'custom';
};

const getOrderType = (
  orderType?: OrderType,
  isRestaurantOutpost?: boolean,
): AddressType => {
  if (isRestaurantOutpost) return 'outpost';

  return orderType === OrderType.Delivery ? 'delivery' : 'pickup';
};

const useOrderStatusCourierPolling = (
  orderId: string,
  orderType: AddressType,
  orderStatus: FlattenedOrderStatuses,
  courierDetails?: CourierDetails | null,
) => {
  const input = { variables: { id: orderId } };
  const timing = useOrderStatusTiming(orderType, orderStatus);
  const { response } = useUrqlPolling(
    timing,
    input,
    useOrderStatusPollingQuery,
  );
  const polledStatus = response?.data?.orderStatus?.flattenedOrderStatus;
  const status = polledStatus ?? orderStatus;
  const courier = response?.data?.orderStatus?.courierDetails ?? courierDetails;

  return { status, courier };
};

const DEFAULT_POLLING_INTERVAL = 1000 * 60; // 60 seconds.
const IN_DELIVERY_POLLING_INTERVAL = 1000 * 10; // 10 seconds.
const PRE_DELIVERY_POLLING_INTERVAL = 1000 * 30; // 30 seconds.
const GEO_POLLING_INTERVAL = 60 * 1000; // 60 seconds.
const useOrderStatusTiming = (
  orderType: AddressType,
  orderStatus: FlattenedOrderStatuses,
) => {
  if (orderStatus === FlattenedOrderStatuses.Delivering) {
    return IN_DELIVERY_POLLING_INTERVAL;
  }

  if (orderType === 'delivery') {
    return PRE_DELIVERY_POLLING_INTERVAL;
  }

  return DEFAULT_POLLING_INTERVAL;
};

const useChallengeCelebration = (orderId: string) => {
  const [
    { data: celebration, fetching: fetchingCelebration },
    fetchChallengeCelebration,
  ] = useChallengeCelebrationQuery({
    pause: true,
    requestPolicy: 'cache-and-network',
  });

  // the useEffect is used to prevent duplicated network requests
  useEffect(() => {
    fetchChallengeCelebration();
  }, [fetchChallengeCelebration, orderId]);

  return useMemo(
    () => ({ celebration, fetchingCelebration }),
    [celebration, fetchingCelebration],
  );
};

export type OrderStatusScreenData = {
  readonly fetching?: boolean;
  readonly deliveryAddressName?: string;
  readonly deliveryAddressStreet?: string;
  readonly deliveryAddressSecondaryStreet?: string;
  readonly deliveryAddressCity?: string;
  readonly deliveryAddressState?: string;
  readonly deliveryAddressZipCode?: string;
  readonly deliveryAddressNotes?: string;
  readonly deliveryAddressLatitude?: number;
  readonly deliveryAddressLongitude?: number;
  readonly deliveryAddressType?: 'home' | 'work' | 'custom';
  readonly deliveryPreference?: DeliveryPreferenceType;
  readonly deliveryEstimatedTime?: string;
  readonly locationName?: string;
  readonly locationAddress?: string;
  readonly locationCity?: string;
  readonly locationState?: string;
  readonly locationZipCode?: string;
  readonly locationLatitude?: number;
  readonly locationLongitude?: number;
  readonly dropoffLocation?: string;
  readonly orderId?: string;
  readonly orderType?: AddressType;
  readonly orderStatus?: FlattenedOrderStatuses;
  readonly orderTotal?: number;
  readonly orderWantedTime?: string;
  readonly orderCanTrackOrderStatus?: boolean;
  readonly orderPendingFeedback?: boolean;
  readonly orderLedger?: Partial<Ledger>;
  readonly orderLineItems?: readonly PartialLineItem[];
  readonly vendorId?: string;
  readonly customerName?: string;
  readonly customerLatitude?: number;
  readonly customerLongitude?: number;
  readonly courierName?: CourierDetails['name'];
  readonly courierPhone?: CourierDetails['phone'];
  readonly courierLatitude?: CourierDetails['latitude'];
  readonly courierLongitude?: CourierDetails['longitude'];
  readonly celebration?: ChallengeCelebration | null;
  readonly isRenderedOnScreen: boolean;
  readonly isInvalidOrderStatus: boolean;
  readonly onSelectRewards?: () => void;
  readonly onReorder: () => void;
};
